import { StatusId, Rang, TabKind, TabDescriptor, OperationBC, CommandeRang } from '../types';

// Logique PURE des onglets acheteur-BC : visibilite des rangs, gating
// (disabled/inactive) et onglet actif initial. Aucune dependance React :
// testable, isolee, reutilisable. Le hook useOperationTabs n'est que le cablage URL.

interface RangState {
  consulte: boolean;
  offreDisponible: boolean;
  accepte: boolean;
  refuse: boolean;
}

const parseRang = (value: string | number): number => parseInt(String(value), 10);
const hasArticles = (cmd: CommandeRang): boolean => Array.isArray(cmd.articles) && cmd.articles.length > 0;
const isRefusedOrExpired = (idStatus: number): boolean =>
  idStatus === StatusId.Refuse || idStatus === StatusId.Expire;

// --- 1. Visibilite des rangs (cascade) ----------------------------------------
// Un rang n'apparait qu'une fois le precedent refuse/expire ; jamais apres un
// rang accepte. Expire == refuse pour le deblocage du rang suivant.
export const computeVisibleRangs = (operation: OperationBC): Rang[] => {
  const commandes = operation.commandes || [];
  const accepts = new Set<number>();
  const refusedOrExpired = new Set<number>();
  const traites = new Set<number>();

  commandes.forEach((cmd) => {
    const rang = parseRang(cmd.rang);
    if (Number.isNaN(rang)) return;
    traites.add(rang);
    if (cmd.id_status === StatusId.Valide) accepts.add(rang);
    else if (isRefusedOrExpired(cmd.id_status)) refusedOrExpired.add(rang);
  });

  const statusNorm = (operation.status?.nom || '').toLowerCase();
  const isTerminee = [StatusId.Valide, StatusId.Refuse, StatusId.Expire].includes(
    operation.id_status as StatusId,
  );
  const visibles = new Set<number>();

  if (statusNorm.includes('brouillon')) {
    visibles.add(1);
  } else if (isTerminee) {
    let rangAccepte = 0;
    accepts.forEach((r) => {
      if (r > rangAccepte) rangAccepte = r;
    });
    if (rangAccepte > 0) {
      for (let r = 1; r <= rangAccepte; r += 1) visibles.add(r);
    } else {
      traites.forEach((r) => visibles.add(r));
    }
  } else {
    visibles.add(1);
    if (refusedOrExpired.has(1)) {
      visibles.add(2);
      if (refusedOrExpired.has(2) && !accepts.has(2)) visibles.add(3);
    }
    if (accepts.has(2)) visibles.add(2);
    if (accepts.has(3)) {
      visibles.add(2);
      visibles.add(3);
    }
  }

  return (Array.from(visibles).filter((r) => r >= 1 && r <= 3).sort((a, b) => a - b)) as Rang[];
};

// --- 2. Construction des descripteurs (ordre = compatibilite index ?tab) ------
const buildBaseDescriptors = (visibleRangs: Rang[]): TabDescriptor[] => {
  const list: TabDescriptor[] = [
    { key: 'historique', kind: 'historique', label: 'Historique', disabled: false, inactive: false },
  ];
  visibleRangs.forEach((rang) => {
    list.push({ key: `consultation-${rang}`, kind: 'consultation', rang, label: `Consultation Rang ${rang}`, disabled: false, inactive: false });
    list.push({ key: `offre-${rang}`, kind: 'offre', rang, label: `Offre Rang ${rang}`, disabled: false, inactive: false });
  });
  list.push({ key: 'notification', kind: 'notification', label: 'Notification et signature', disabled: false, inactive: false });
  list.push({ key: 'messagerie', kind: 'messagerie', label: 'Messagerie', disabled: false, inactive: false });
  list.push({ key: 'evaluation', kind: 'evaluation', label: 'Évaluation', disabled: false, inactive: false });
  return list;
};

// --- 3. Etat par rang (entree du gating) --------------------------------------
const computeRangStates = (operation: OperationBC): Map<number, RangState> => {
  const states = new Map<number, RangState>();
  for (let r = 1; r <= 3; r += 1) {
    states.set(r, { consulte: false, offreDisponible: false, accepte: false, refuse: false });
  }

  const commandes = operation.commandes || [];
  const isTransferred = operation.transferred === true;
  const status = operation.status?.nom;
  const statusNorm = (status || '').toLowerCase();
  const currentRang = operation.rang_actuel || 1;

  commandes.forEach((cmd) => {
    const state = states.get(parseRang(cmd.rang));
    if (!state) return;
    state.consulte = true;
    if (!isTransferred && hasArticles(cmd)) state.offreDisponible = true;
    switch (cmd.id_status) {
      case StatusId.Valide:
        state.accepte = true;
        state.offreDisponible = true;
        break;
      case StatusId.Refuse:
      case StatusId.Expire: // expire == refuse pour la cascade
        state.refuse = true;
        state.offreDisponible = true;
        break;
      case StatusId.AttenteAnalyse:
        state.offreDisponible = true;
        break;
      default:
        break;
    }
  });

  if (isTransferred) {
    for (let rang = 1; rang <= 2; rang += 1) {
      const state = states.get(rang);
      const next = states.get(rang + 1);
      if (state?.refuse && next) next.consulte = true;
    }
    commandes.forEach((cmd) => {
      if (cmd.id_status === StatusId.PropositionFaite) {
        const state = states.get(parseRang(cmd.rang));
        if (state) state.offreDisponible = true;
      }
    });
  }

  if (status === 'À accepter') {
    const state = states.get(currentRang);
    if (state) state.offreDisponible = true;
  }
  if (!isTransferred && status === 'À analyser') {
    commandes.forEach((cmd) => {
      const state = states.get(parseRang(cmd.rang));
      if (state && (hasArticles(cmd) || isRefusedOrExpired(cmd.id_status))) state.offreDisponible = true;
    });
  }
  if (statusNorm === 'refusé' || statusNorm === 'refuse') {
    const state = states.get(currentRang);
    if (state) state.offreDisponible = true;
  }

  return states;
};

// --- 4. Gating : une regle simple par type d'onglet ---------------------------
// Chaque fonction repond a UNE question : "cet onglet est-il desactive ?".
interface GateContext {
  operation: OperationBC;
  states: Map<number, RangState>;
  isBrouillon: boolean;
  isTransferred: boolean;
  currentRang: number;
}

const consultationDisabled = (rang: Rang, ctx: GateContext): boolean => {
  const state = ctx.states.get(rang);
  const accessible = Boolean(state?.consulte) || rang === ctx.currentRang;
  return !accessible;
};

const offreDisabled = (rang: Rang, ctx: GateContext): boolean => {
  const state = ctx.states.get(rang);
  const cmd = (ctx.operation.commandes || []).find((c) => parseRang(c.rang) === rang);
  if (ctx.isTransferred) {
    const offrePrestataireDispo =
      !!cmd && [StatusId.PropositionFaite, StatusId.Valide, StatusId.Refuse].includes(cmd.id_status);
    return !(state?.offreDisponible || offrePrestataireDispo);
  }
  if (rang > 1 && cmd?.id_status === StatusId.BrouillonAcheteur) return true; // consultation pas lancee
  return !state?.offreDisponible;
};

// En brouillon : seuls Historique, Consultation Rang 1, Messagerie, Evaluation accessibles.
const allowedInBrouillon = (tab: TabDescriptor): boolean =>
  tab.kind === 'historique' ||
  (tab.kind === 'consultation' && tab.rang === 1) ||
  tab.kind === 'messagerie' ||
  tab.kind === 'evaluation';

const applyGating = (descriptors: TabDescriptor[], operation: OperationBC): TabDescriptor[] => {
  const ctx: GateContext = {
    operation,
    states: computeRangStates(operation),
    isBrouillon: (operation.status?.nom || '').toLowerCase().includes('brouillon'),
    isTransferred: operation.transferred === true,
    currentRang: operation.rang_actuel || 1,
  };

  // Evaluation : active, mais grisee tant qu'aucune commande n'est validee (acceptee = 7).
  const evalUnlocked = (operation.commandes || []).some((c) => c.id_status === StatusId.Valide);

  return descriptors.map((tab) => {
    let disabled = false;
    if (tab.kind === 'messagerie') {
      // Messagerie privee : active uniquement quand l'operation est validee (commande acceptee = 7).
      // Grisee (non "a venir") tant que le prestataire n'est pas retenu.
      disabled = operation.id_status !== StatusId.Valide;
    } else if (tab.kind === 'notification') {
      // Notification et signature : accessible UNIQUEMENT quand le bon de commande est accepte/signe
      // des deux cotes (operation au statut Valide=7), comme la messagerie.
      disabled = operation.id_status !== StatusId.Valide;
    } else if (tab.kind === 'evaluation') {
      disabled = !evalUnlocked;
    } else if (ctx.isBrouillon) {
      disabled = !allowedInBrouillon(tab);
    } else if (tab.kind === 'consultation' && tab.rang) {
      disabled = consultationDisabled(tab.rang, ctx);
    } else if (tab.kind === 'offre' && tab.rang) {
      disabled = offreDisabled(tab.rang, ctx);
    }

    return { ...tab, disabled, inactive: false };
  });
};

// --- 5. API publique du domaine -----------------------------------------------
export const buildOperationTabs = (operation: OperationBC): TabDescriptor[] =>
  applyGating(buildBaseDescriptors(computeVisibleRangs(operation)), operation);

// Onglet actif par defaut (hors URL ?tab) selon le statut de l'operation.
export const computeDefaultTabIndex = (descriptors: TabDescriptor[], operation: OperationBC): number => {
  const indexOf = (kind: TabKind, rang?: number) =>
    descriptors.findIndex((t) => t.kind === kind && (rang === undefined || t.rang === rang));

  const status = operation.status?.nom;
  const statusNorm = (status || '').toLowerCase();
  const isTransferred = operation.transferred === true;
  const commandes = operation.commandes || [];
  const operationStatusId = operation.id_status;

  if (statusNorm.includes('brouillon')) {
    const i = indexOf('consultation', 1);
    return i === -1 ? 0 : i;
  }

  // Operation validee : on ouvre "Notifications et signature", pas l'Offre. Meme regle que le clic
  // depuis la liste des operations (acheteurRowTabKind) — sans ca, l'acheteur qui vient de signer et
  // dont l'URL ne porte pas de ?tab= retombait sur l'Offre au rechargement. Repli sur l'offre du rang
  // accepte si l'onglet notification n'existe pas dans ce jeu de descripteurs.
  if (operationStatusId === StatusId.Valide) {
    const notif = indexOf('notification');
    if (notif !== -1) return notif;
    const accepted = commandes.find((c) => c.id_status === StatusId.Valide);
    if (accepted) {
      const i = indexOf('offre', parseRang(accepted.rang));
      if (i !== -1) return i;
    }
  }

  if (!isTransferred && (status === 'À analyser' || operationStatusId === StatusId.AAnalyser)) {
    if (commandes.length === 0) return 0;
    const infoByRang = (rang: number) => {
      const cmd = commandes.find((c) => parseRang(c.rang) === rang);
      return cmd ? { hasArticles: hasArticles(cmd), refuseOuExpire: isRefusedOrExpired(cmd.id_status) } : null;
    };
    const r1 = infoByRang(1);
    const r2 = infoByRang(2);
    const r3 = infoByRang(3);
    if (r1?.refuseOuExpire && r2?.hasArticles) {
      const i = indexOf('offre', 2);
      if (i !== -1) return i;
    }
    if (r1?.refuseOuExpire && r2?.refuseOuExpire && r3?.hasArticles) {
      const i = indexOf('offre', 3);
      if (i !== -1) return i;
    }
    const i = indexOf('offre', 1);
    return i === -1 ? 0 : i;
  }

  if (
    isTransferred &&
    (status === 'À analyser' ||
      status === "En attente de décision de l'acheteur" ||
      operationStatusId === StatusId.AttenteAnalyse)
  ) {
    const ordered = commandes
      .map((c) => ({ rang: parseRang(c.rang), refuseOuExpire: isRefusedOrExpired(c.id_status) }))
      .sort((a, b) => a.rang - b.rang);
    const firstActive = ordered.find((r) => !r.refuseOuExpire);
    const target = firstActive ? indexOf('consultation', firstActive.rang) : indexOf('consultation', 1);
    return target === -1 ? 0 : target;
  }

  return 0;
};
